import { brand } from "../../../../../../config/theme";
import { useState, useEffect, useMemo, useRef, type ChangeEvent } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import { useCompaniesStore } from "../../../../../../store/admin/companiesStore";
import { useCompaniesList } from "../../../../../../features/companies/queries";
import { useDevicesStore } from "../../../../../../store/admin/devicesStore";
import { getError } from "../../../../../../utils/getError";
import { archiveOrAlert } from "../../../../../../features/device-archive/jobs";
import { pickBackupSources } from "../../../../../../features/device-archive/backupModal";
import { SEARCH_DEBOUNCE_MS } from "../../../../../../hooks/useDebounce";
import type { Company, AlertState, FilterParams } from "../types";
import type { CompanyFormData } from "../components/CompanyAddModal";

export function useCompanyForm() {
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"details" | "edit" | "add">(
    "details",
  );
  const [alert, setAlert] = useState<AlertState>({
    show: false,
    title: "",
    message: "",
    type: "info",
  });

  // Add modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [editCompanyId, setEditCompanyId] = useState<string | null>(null);
  const [originalCompanyData, setOriginalCompanyData] = useState<any>(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const [actionLabel, setActionLabel] = useState("Procesando…");
  const [actionIcon, setActionIcon] = useState("ri-loader-4-line");

  // Filter states
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchInput, setSearchInput] = useState(""); // Valor inmediato del input
  const [searchQuery, setSearchQuery] = useState(""); // Valor con debounce para la API
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const itemsPerPage = 6;
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce de la búsqueda para no disparar una petición por cada tecla
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchInput]);

  const queryClient = useQueryClient();
  const {
    fetchCompany,
    createCompany,
    updateCompany,
    deleteCompany,
    fetchAllCompanies,
    companyId: selectedCompanyId,
    setCompanyId,
  } = useCompaniesStore();
  const { devices, fetchDevices } = useDevicesStore();

  const filterParams = useMemo<FilterParams>(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      status: selectedStatus !== "all" ? selectedStatus : undefined,
      name: searchQuery || undefined,
    }),
    [currentPage, itemsPerPage, selectedStatus, searchQuery],
  );

  const companiesQuery = useCompaniesList(filterParams);
  const companies = companiesQuery.data?.data ?? [];
  const meta = companiesQuery.data?.meta;
  const loading = companiesQuery.isLoading;

  useEffect(() => {
    if (companiesQuery.data) {
      useCompaniesStore.setState({
        companies: companiesQuery.data.data,
        meta: companiesQuery.data.meta,
      });
    }
  }, [companiesQuery.data]);

  const invalidateCompanies = () => {
    queryClient.invalidateQueries({ queryKey: ["companies"] });
    fetchAllCompanies(true);
  };

  // Fetch devices on mount
  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // React Hook Form
  const form = useForm<CompanyFormData>({
    defaultValues: {
      name: "",
      business_name: "",
      carrier_id: "",
      email: "",
      phone: "",
      rfc: "",
      status_id: "",
      origin: [],
      account_condition: "",
      main_user: {
        user_name: "",
        first_name: "",
        last_name: "",
        email: "",
        username: "",
        password: "",
        map_interval: 4,
        not_reporting_alert: true,
      },
      website: "",
      contact_person: {
        name: "",
        phone: "",
        email: "",
      },
      contact_address: {
        address1: "",
        address2: "",
        city: "",
        state: "",
        country: "",
        zip_code: "",
      },
      billing_address: {
        address1: "",
        address2: "",
        city: "",
        state: "",
        country: "",
        zip_code: "",
      },
      devices: [],
    },
  });

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    // El reset de página se maneja en el efecto de debounce
  };

  const handleDetailsClick = (company: Company) => {
    setSelectedCompany(company);
    setModalType("details");
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleEditClick = async (company: Company) => {
    try {
      // Intentar obtener datos completos de la compañía desde la API
      const fullCompany: any = await fetchCompany(company.id);

      // Extraer datos del API (vienen anidados en .data)
      const apiData = fullCompany?.data || fullCompany;
      const companyData = apiData || company;

      // PRIMERO: Reset del formulario con los datos (igual que TrackingLinks)
      const resetData = {
        name: companyData.name || "",
        business_name: companyData.business_name || "",
        carrier_id: companyData.id || "",
        email: companyData.email || "",
        phone: companyData.phone || "",
        rfc: companyData.rfc || "",
        status_id: companyData.status_id || "jXmjzHcBD5E1J0wjwHzq",
        website: companyData.website || "",
        origin: Array.isArray(companyData.origin) ? companyData.origin : [],
        account_condition: companyData.account_condition || "",
        main_user: {
          user_name: companyData.main_user?.user_name || "",
          first_name: companyData.main_user?.first_name || "",
          last_name: companyData.main_user?.last_name || "",
          email: companyData.main_user?.email || "",
          username: companyData.main_user?.username || "",
          password: "",
          map_interval: companyData.main_user?.map_interval ?? 4,
          not_reporting_alert:
            companyData.main_user?.not_reporting_alert ?? true,
        },
        contact_person: {
          name: companyData.contact_person?.name || "",
          phone: companyData.contact_person?.phone || "",
          email: companyData.contact_person?.email || "",
        },
        contact_address: {
          address1: companyData.contact_address?.address1 || "",
          address2: companyData.contact_address?.address2 || "",
          city: companyData.contact_address?.city || "",
          state: companyData.contact_address?.state || "",
          country: companyData.contact_address?.country || "",
          zip_code: companyData.contact_address?.zip_code || "",
        },
        billing_address: {
          address1: companyData.billing_address?.address1 || "",
          address2: companyData.billing_address?.address2 || "",
          city: companyData.billing_address?.city || "",
          state: companyData.billing_address?.state || "",
          country: companyData.billing_address?.country || "",
          zip_code: companyData.billing_address?.zip_code || "",
        },
        devices: companyData.devices || [],
      };

      form.reset(resetData);

      // Cargar preview del logo si existe
      if (companyData.logo) {
        setImagePreview(companyData.logo);
      } else {
        setImagePreview(null);
      }

      // Guardar datos originales para comparación
      setOriginalCompanyData(companyData);

      // DESPUÉS: Establecer estados y abrir modal
      setEditCompanyId(company.id);
      setIsEdit(true);
      setShowAddModal(true);
      document.body.style.overflow = "hidden";
    } catch (error) {
      console.error("❌ Error al cargar compañía:", error);

      const companyData: any = company;

      const resetData = {
        name: companyData.name || "",
        business_name: companyData.business_name || "",
        carrier_id: companyData.id || "",
        email: companyData.email || "",
        phone: companyData.phone || "",
        rfc: companyData.rfc || "",
        status_id: companyData.status_id || "jXmjzHcBD5E1J0wjwHzq",
        website: companyData.website || "",
        origin: Array.isArray(companyData.origin) ? companyData.origin : [],
        account_condition: companyData.account_condition || "",
        main_user: {
          user_name: companyData.main_user?.user_name || "",
          first_name: companyData.main_user?.first_name || "",
          last_name: companyData.main_user?.last_name || "",
          email: companyData.main_user?.email || "",
          username: companyData.main_user?.username || "",
          password: "",
          map_interval: companyData.main_user?.map_interval ?? 4,
          not_reporting_alert:
            companyData.main_user?.not_reporting_alert ?? true,
        },
        contact_person: {
          name: companyData.contact_person?.name || "",
          phone: companyData.contact_person?.phone || "",
          email: companyData.contact_person?.email || "",
        },
        contact_address: {
          address1: companyData.contact_address?.address1 || "",
          address2: companyData.contact_address?.address2 || "",
          city: companyData.contact_address?.city || "",
          state: companyData.contact_address?.state || "",
          country: companyData.contact_address?.country || "",
          zip_code: companyData.contact_address?.zip_code || "",
        },
        billing_address: {
          address1: companyData.billing_address?.address1 || "",
          address2: companyData.billing_address?.address2 || "",
          city: companyData.billing_address?.city || "",
          state: companyData.billing_address?.state || "",
          country: companyData.billing_address?.country || "",
          zip_code: companyData.billing_address?.zip_code || "",
        },
        devices: companyData.devices || [],
      };

      form.reset(resetData);

      // Cargar preview del logo
      if (companyData.logo) {
        setImagePreview(companyData.logo);
      } else {
        setImagePreview(null);
      }

      // Guardar datos originales para comparación
      setOriginalCompanyData(companyData);

      // DESPUÉS: Establecer estados y abrir modal
      setEditCompanyId(company.id);
      setIsEdit(true);
      setShowAddModal(true);
      document.body.style.overflow = "hidden";
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCompany(null);
    // Limpiar formulario al cerrar modal de edición
    form.reset();
    document.body.style.overflow = "";
  };

  const closeAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };

  // Valores por defecto del formulario
  const defaultFormValues: CompanyFormData = {
    name: "",
    business_name: "",
    carrier_id: "",
    email: "",
    phone: "",
    rfc: "",
    status_id: "",
    origin: [],
    account_condition: "",
    main_user: {
      user_name: "",
      first_name: "",
      last_name: "",
      email: "",
      username: "",
      password: "",
      map_interval: 4,
      not_reporting_alert: true,
    },
    website: "",
    contact_person: {
      name: "",
      phone: "",
      email: "",
    },
    contact_address: {
      address1: "",
      address2: "",
      city: "",
      state: "",
      country: "",
      zip_code: "",
    },
    billing_address: {
      address1: "",
      address2: "",
      city: "",
      state: "",
      country: "",
      zip_code: "",
    },
    devices: [],
  };

  // Add modal handlers
  const handleAddClick = () => {
    setIsEdit(false);
    setEditCompanyId(null);
    setOriginalCompanyData(null);
    form.reset(defaultFormValues);
    setImagePreview(null);
    setImageFile(null);
    setShowAddModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setIsEdit(false);
    setEditCompanyId(null);
    setOriginalCompanyData(null);
    setLoadingSubmit(false);
    form.reset(defaultFormValues);
    setImagePreview(null);
    setImageFile(null);
    document.body.style.overflow = "";
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Función para verificar si hay cambios reales
  const hasFormChanges = () => {
    if (!isEdit || !originalCompanyData) return false;

    const currentValues = form.getValues();

    // Comparar campos básicos
    const basicFieldsChanged =
      currentValues.name !== originalCompanyData.name ||
      currentValues.business_name !== originalCompanyData.business_name ||
      currentValues.email !== originalCompanyData.email ||
      currentValues.phone !== originalCompanyData.phone ||
      currentValues.rfc !== originalCompanyData.rfc ||
      currentValues.status_id !== originalCompanyData.status_id ||
      currentValues.website !== originalCompanyData.website;

    // Comparar arrays (devices)
    const devicesChanged =
      JSON.stringify(currentValues.devices?.sort()) !==
      JSON.stringify(originalCompanyData.devices?.sort());

    const originChanged =
      JSON.stringify([...(currentValues.origin || [])].sort()) !==
      JSON.stringify([...(originalCompanyData.origin || [])].sort());

    // Comparar objetos anidados
    const compareNested = (current: any, original: any) => {
      return JSON.stringify(current) !== JSON.stringify(original);
    };

    const contactPersonChanged = compareNested(
      currentValues.contact_person,
      originalCompanyData.contact_person,
    );
    const contactAddressChanged = compareNested(
      currentValues.contact_address,
      originalCompanyData.contact_address,
    );
    const billingAddressChanged = compareNested(
      currentValues.billing_address,
      originalCompanyData.billing_address,
    );

    // Verificar si el logo cambió
    const logoChanged = imagePreview !== originalCompanyData.logo;

    const hasChanges =
      basicFieldsChanged ||
      devicesChanged ||
      originChanged ||
      contactPersonChanged ||
      contactAddressChanged ||
      billingAddressChanged ||
      logoChanged;

    return hasChanges;
  };

  const handleSuspendCompany = async (company: Company) => {
    const choice = await pickBackupSources("suspend");
    if (choice.action === "cancel") return;

    setActionLabel("Suspendiendo compañía…");
    setActionIcon("ri-pause-circle-line");
    setLoadingAction(true);
    try {
      await updateCompany(company.id, {
        status_id: "Y2aD7bE1fF6jM3kO9hP5"
      });

      if (choice.action === "archive") {
        await archiveOrAlert(company.id, "suspend", choice.sources);
      }

      invalidateCompanies();
      closeModal();
      closeAddModal();

      await Swal.fire({
        icon: "success",
        title: "Compañía suspendida",
        timer: 1800,
        showConfirmButton: false,
        timerProgressBar: true,
      });
    } catch (error) {
      console.error("Error al suspender compañía:", error);
      const backendMessage = getError(error);
      await Swal.fire({
        title: "Error",
        text:
          backendMessage && backendMessage !== "Error desconocido"
            ? backendMessage
            : "No se pudo suspender la compañía",
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleActivateCompany = async (company: Company) => {
    // console.log("Activation started for:", company.name);

    try {
      const result = await Swal.fire({
        title: "Activar Compañía",
        text: `¿Está seguro de que desea activar la compañía "${company.name}"? Esta acción restaurará el acceso completo a la compañía.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Sí, activar",
        cancelButtonText: "Cancelar",
      });

      // console.log("SweetAlert result:", result);

      if (!result.isConfirmed) {
        // console.log("User cancelled activation");
        return;
      }

      setActionLabel("Activando compañía…");
      setActionIcon("ri-checkbox-circle-line");
      setLoadingAction(true);

      // console.log("Updating company status...");
      // console.log("Activating company:", company.id, "with status: active and status_id: jXmjzHcBD5E1J0wjwHzq");
      await updateCompany(company.id, {
        status_id: "jXmjzHcBD5E1J0wjwHzq"
      });

      // console.log("Company updated successfully");
      await Swal.fire({
        title: "¡Compañía Activada!",
        text: `La compañía "${company.name}" ha sido activada correctamente`,
        icon: "success",
        confirmButtonText: "Aceptar",
        confirmButtonColor: brand.primary,
      });

      invalidateCompanies();
      closeModal();
      closeAddModal();
    } catch (error) {
      console.error("Error al activar compañía:", error);

      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      const errorStack = error instanceof Error ? error.stack : 'No stack trace available';

      console.error("Error details:", errorMessage);
      console.error("Error stack:", errorStack);

      // Fallback to browser alert if SweetAlert fails
      if (errorMessage && errorMessage.includes('SweetAlert')) {
        window.alert(`Error: ${errorMessage}. Using fallback alert.`);
      }

      const backendMessage = getError(error);
      await Swal.fire({
        title: "Error",
        text:
          backendMessage && backendMessage !== "Error desconocido"
            ? backendMessage
            : "No se pudo activar la compañía",
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDeleteCompany = async (company: Company) => {
    const linkedV5 = (company.origin ?? []).includes("v5");
    const baseText =
      "El borrado es reversible: la compañía se moverá a la Papelera y podrás restaurarla.";
    const result = await Swal.fire({
      title: "Eliminar Compañía",
      html: linkedV5
        ? `${baseText}<br/><br/><strong>Esta compañía está vinculada con SG.</strong> Este borrado reversible <u>no</u> elimina la cuenta en SG.`
        : baseText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    setActionLabel("Eliminando compañía…");
    setActionIcon("ri-delete-bin-line");
    setLoadingAction(true);
    try {
      await deleteCompany(company.id);

      // Si la compañía borrada era la seleccionada en el header, limpiar la
      // selección para no quedar apuntando a un id que ya no existe.
      if (selectedCompanyId === company.id) {
        setCompanyId(null);
      }

      // Quitar la compañía del selector (allCompanies) sin recarga completa y
      // refrescar la lista vía TanStack (en vez de fetchAllCompanies(true)).
      useCompaniesStore.setState((state) => ({
        allCompanies: state.allCompanies.filter((c) => c.id !== company.id),
      }));
      queryClient.invalidateQueries({ queryKey: ["companies"] });

      await Swal.fire({
        title: "¡Compañía Eliminada!",
        text: `La compañía "${company.name}" ha sido eliminada correctamente`,
        icon: "success",
        confirmButtonText: "Aceptar",
        confirmButtonColor: brand.primary,
      });

      closeModal();
      closeAddModal();
    } catch (error) {
      console.error("Error al eliminar compañía:", error);
      const backendMessage = getError(error);
      await Swal.fire({
        title: "Error",
        text:
          backendMessage && backendMessage !== "Error desconocido"
            ? backendMessage
            : "No se pudo eliminar la compañía",
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleSubmitCompany = async (data: CompanyFormData) => {
    setLoadingSubmit(true);
    try {
      // Preparar datos para la API
      const companyData = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        business_name: data.business_name,
        status_id: data.status_id || "jXmjzHcBD5E1J0wjwHzq",
        rfc: data.rfc || undefined,
        website: data.website || undefined,
        origin: data.origin && data.origin.length > 0 ? data.origin : undefined,
        account_condition: data.account_condition || undefined,
        main_user: data.main_user,
        carrierId: data.carrier_id || undefined,
        contact_person: {
          name: data.contact_person.name,
          phone: data.contact_person.phone,
          email: data.contact_person.email,
        },
        contact_address: {
          address1: data.contact_address.address1,
          address2: data.contact_address.address2 || undefined,
          city: data.contact_address.city,
          state: data.contact_address.state,
          country: data.contact_address.country,
          zip_code: data.contact_address.zip_code,
        },
        billing_address: data.billing_address.address1
          ? {
              address1: data.billing_address.address1 || undefined,
              address2: data.billing_address.address2 || undefined,
              city: data.billing_address.city || undefined,
              state: data.billing_address.state || undefined,
              country: data.billing_address.country || undefined,
              zip_code: data.billing_address.zip_code || undefined,
            }
          : undefined,
        devices: data.devices.length > 0 ? data.devices : undefined,
      };

      // Llamar a la API según el modo (crear o editar)
      if (isEdit && editCompanyId) {
        await updateCompany(editCompanyId, companyData, imageFile);
        await Swal.fire({
          title: "¡Éxito!",
          text: "Compañía actualizada correctamente",
          icon: "success",
          confirmButtonText: "Aceptar",
          confirmButtonColor: brand.primary,
        });
      } else {
        await createCompany(companyData, imageFile);
        await Swal.fire({
          title: "¡Éxito!",
          text: "Compañía creada correctamente",
          icon: "success",
          confirmButtonText: "Aceptar",
          confirmButtonColor: brand.primary,
        });
      }

      closeAddModal();
      form.reset();
      invalidateCompanies();
    } catch (error: any) {
      console.error("Error al guardar compañía:", error);

      // Extraer el mensaje real del backend (incluye errores de validación 422)
      const backendMessage = getError(error);
      const fallbackMessage = isEdit
        ? "Error al actualizar la compañía"
        : "Error al crear la compañía";

      // SweetAlert de error (no cierra el modal)
      await Swal.fire({
        title: "Error",
        text:
          backendMessage && backendMessage !== "Error desconocido"
            ? backendMessage
            : fallbackMessage,
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  return {
    // Data
    companies,
    meta,
    loading,
    selectedCompany,
    alert,
    devices,

    // Modal states
    isModalOpen,
    modalType,
    showAddModal,
    loadingSubmit,
    imagePreview,
    isEdit,
    editCompanyId,

    // Form
    form,

    // Filter states
    selectedStatus,
    searchQuery: searchInput,
    currentPage,
    selectedView,
    itemsPerPage,

    // Handlers
    handleStatusChange,
    handleSearchChange,
    handleDetailsClick,
    handleEditClick,
    handleAddClick,
    handleImageChange,
    handleSubmitCompany,
    setCurrentPage,
    setSelectedView,
    closeModal,
    closeAddModal,
    closeAlert,
    hasFormChanges,
    handleSuspendCompany,
    handleActivateCompany,
    handleDeleteCompany,

    // Action loading state
    loadingAction,
    actionLabel,
    actionIcon,
  };
}
